import Lenis from "lenis";

export interface ScrollToOptions {
  immediate?: boolean;
  force?: boolean;
  duration?: number;
  lock?: boolean;
  onComplete?: () => void;
}

export interface ScrollEvent {
  /** Pixels per frame, the scale `HomeScene.setScrollMotion` is tuned against. */
  velocity: number;
  /** 1 down, -1 up, 0 at rest. */
  direction: number;
}

export type ScrollListener = (event: ScrollEvent) => void;

export interface ScrollController {
  readonly smooth: boolean;
  on(event: "scroll", listener: ScrollListener): () => void;
  scrollTo(target: HTMLElement | number, options?: ScrollToOptions): void;
  resize(): void;
  start(): void;
  stop(): void;
  destroy(): void;
}

const FRAME_MS = 1_000 / 60;
const SETTLE_TIMEOUT_MS = 1_600;
const SETTLE_TOLERANCE_PX = 2;
const SETTLE_STILL_FRAMES = 3;

function documentTop(target: HTMLElement | number): number {
  return typeof target === "number" ? target : target.getBoundingClientRect().top + window.scrollY;
}

class SmoothScrollController implements ScrollController {
  readonly smooth = true;
  readonly #lenis = new Lenis({
    autoRaf: true,
    lerp: 0.1,
    duration: 1.5,
    syncTouch: true,
    anchors: false,
    stopInertiaOnNavigate: true,
    respectReducedMotion: true,
  });

  on(_event: "scroll", listener: ScrollListener): () => void {
    return this.#lenis.on("scroll", (instance: Lenis) => listener({ velocity: instance.velocity, direction: instance.direction }));
  }

  scrollTo(target: HTMLElement | number, options: ScrollToOptions = {}): void {
    this.#lenis.scrollTo(target, options);
  }

  resize(): void {
    this.#lenis.resize();
  }

  start(): void {
    this.#lenis.start();
  }

  stop(): void {
    this.#lenis.stop();
  }

  destroy(): void {
    this.#lenis.destroy();
  }
}

/**
 * Native scrolling for touch devices. Lenis with `syncTouch` takes the gesture away from the
 * compositor and re-drives it in JavaScript, which is exactly the work a phone cannot spare.
 */
class NativeScrollController implements ScrollController {
  readonly smooth = false;
  readonly #listeners = new Set<ScrollListener>();
  readonly #handleScroll = (): void => {
    const position = window.scrollY;
    const now = performance.now();
    const elapsed = Math.max(now - this.#lastTime, 1);
    const distance = position - this.#lastPosition;
    const velocity = (distance / elapsed) * FRAME_MS;
    const direction = distance > 0 ? 1 : distance < 0 ? -1 : 0;
    this.#lastPosition = position;
    this.#lastTime = now;
    this.#listeners.forEach((listener) => listener({ velocity, direction }));
  };
  #lastPosition = window.scrollY;
  #lastTime = performance.now();
  #settleFrame = 0;

  constructor() {
    window.addEventListener("scroll", this.#handleScroll, { passive: true });
  }

  on(_event: "scroll", listener: ScrollListener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  scrollTo(target: HTMLElement | number, options: ScrollToOptions = {}): void {
    const top = documentTop(target);
    const immediate = options.immediate === true || window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    cancelAnimationFrame(this.#settleFrame);
    window.scrollTo({ top, behavior: immediate ? "instant" : "smooth" });
    const onComplete = options.onComplete;
    if (!onComplete) return;
    if (immediate) {
      onComplete();
      return;
    }
    this.#settleAt(top, onComplete);
  }

  // Native smooth scrolling has no completion callback we can rely on across browsers, so the
  // caller is told once the page stops moving. Callers keep their own timeout as a backstop.
  #settleAt(top: number, onComplete: () => void): void {
    const startedAt = performance.now();
    let lastPosition = Number.NaN;
    let stillFrames = 0;
    const check = (): void => {
      const position = window.scrollY;
      const arrived = Math.abs(position - top) <= SETTLE_TOLERANCE_PX;
      stillFrames = position === lastPosition ? stillFrames + 1 : 0;
      lastPosition = position;
      if (arrived || stillFrames >= SETTLE_STILL_FRAMES || performance.now() - startedAt > SETTLE_TIMEOUT_MS) {
        onComplete();
        return;
      }
      this.#settleFrame = requestAnimationFrame(check);
    };
    this.#settleFrame = requestAnimationFrame(check);
  }

  // The page is locked through `body.is-loading` and `body.is-transitioning` in CSS, so native
  // scrolling has nothing to start or stop.
  resize(): void {}

  start(): void {}

  stop(): void {}

  destroy(): void {
    window.removeEventListener("scroll", this.#handleScroll);
    cancelAnimationFrame(this.#settleFrame);
    this.#listeners.clear();
  }
}

const NATIVE_SCROLL_QUERY = "(hover: none), (pointer: coarse), (max-width: 760px), (prefers-reduced-motion: reduce)";

export function createScrollController(): ScrollController {
  return window.matchMedia(NATIVE_SCROLL_QUERY).matches ? new NativeScrollController() : new SmoothScrollController();
}
